import React from "react";
import { BiEnvelope, BiMessageDetail, BiEdit } from "react-icons/bi";
import sideCars from "../../assets/aboutus/side-cars-aboutus-cut.png";
import { TfiEmail } from "react-icons/tfi";

export default function NeedToGetInTouch({
  section, // { heading, copy, cards: [ { icon, title, lines:[…] }, … ] }
  pageContent,
  setPageContent,
  editingSection,
  setEditingSection,
  isAdmin,
  saveContent,
}) {
  const renderLine = (line, idx) => {
    if (typeof line === "string") {
      return <div key={idx}>{line}</div>;
    }
    if (typeof line === "object" && line !== null) {
      if (line.type === "email" && line.value) {
        return (
          <div key={idx}>
            <a
              href={`mailto:${line.value}`}
              className="text-primary hover:underline"
            >
              {line.value}
            </a>
          </div>
        );
      }
      if (line.type === "phone" && line.value) {
        return (
          <div key={idx}>
            <a
              href={`tel:${line.value}`}
              className="text-primary hover:underline"
            >
              {line.value}
            </a>
          </div>
        );
      }
      if (line.type === "link" && line.text && line.href) {
        return (
          <div key={idx}>
            <a
              href={line.href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              {line.text}
            </a>
          </div>
        );
      }
      // Fallback: render JSON
      return (
        <div key={idx} className="text-gray-600 italic text-sm">
          {JSON.stringify(line)}
        </div>
      );
    }
    return null;
  };

  // Update section heading or copy:
  const updateSectionField = (field, value) => {
    setPageContent((prev) => {
      const newSections = prev.sections.map((sec) =>
        sec.key === "need-to-get-in-touch" ? { ...sec, [field]: value } : sec
      );
      return { ...prev, sections: newSections };
    });
  };

  // Update a card’s title:
  const updateCardTitle = (cardIdx, newTitle) => {
    setPageContent((prev) => {
      const newSections = prev.sections.map((sec) => {
        if (sec.key !== "need-to-get-in-touch") return sec;
        const newCards = sec.cards.map((c, i) =>
          i === cardIdx ? { ...c, title: newTitle } : c
        );
        return { ...sec, cards: newCards };
      });
      return { ...prev, sections: newSections };
    });
  };

  // Update one line within a card:
  const updateCardLine = (cardIdx, lineIdx, newLine) => {
    setPageContent((prev) => {
      const newSections = prev.sections.map((sec) => {
        if (sec.key !== "need-to-get-in-touch") return sec;
        const newCards = sec.cards.map((c, i) => {
          if (i !== cardIdx) return c;
          const newLines = c.lines.map((l, j) => (j === lineIdx ? newLine : l));
          return { ...c, lines: newLines };
        });
        return { ...sec, cards: newCards };
      });
      return { ...prev, sections: newSections };
    });
  };

  // Add a new (empty) line to a card:
  const addCardLine = (cardIdx) => {
    setPageContent((prev) => {
      const newSections = prev.sections.map((sec) => {
        if (sec.key !== "need-to-get-in-touch") return sec;
        const newCards = sec.cards.map((c, i) => {
          if (i !== cardIdx) return c;
          // Default new line is a plain string
          return { ...c, lines: [...c.lines, ""] };
        });
        return { ...sec, cards: newCards };
      });
      return { ...prev, sections: newSections };
    });
  };

  // Remove a line from a card:
  const removeCardLine = (cardIdx, lineIdx) => {
    setPageContent((prev) => {
      const newSections = prev.sections.map((sec) => {
        if (sec.key !== "need-to-get-in-touch") return sec;
        const newCards = sec.cards.map((c, i) => {
          if (i !== cardIdx) return c;
          const newLines = c.lines.filter((_, j) => j !== lineIdx);
          return { ...c, lines: newLines };
        });
        return { ...sec, cards: newCards };
      });
      return { ...prev, sections: newSections };
    });
  };

  // Change an existing line’s “type” (string vs email vs phone vs link)
  const changeLineType = (cardIdx, lineIdx, newType) => {
    setPageContent((prev) => {
      const newSections = prev.sections.map((sec) => {
        if (sec.key !== "need-to-get-in-touch") return sec;
        const newCards = sec.cards.map((c, i) => {
          if (i !== cardIdx) return c;
          const newLines = c.lines.map((l, j) => {
            if (j !== lineIdx) return l;
            // If switching to “string”, reset to empty string
            if (newType === "string") return "";
            // If switching to email or phone, use an object with value = ""
            if (newType === "email" || newType === "phone") {
              return { type: newType, value: "" };
            }
            // If “link”, default to {type:'link', text:'', href:''}
            if (newType === "link") {
              return { type: newType, text: "", href: "" };
            }
            return l;
          });
          return { ...c, lines: newLines };
        });
        return { ...sec, cards: newCards };
      });
      return { ...prev, sections: newSections };
    });
  };

  return (
    <section className="bg-accent py-10 md:pt-28 relative">
      {/* ─── Admin‐only edit icon ───────────────────────────────── */}
      {isAdmin && editingSection !== "need-to-get-in-touch" && (
        <BiEdit
          onClick={() => setEditingSection("need-to-get-in-touch")}
          className="absolute top-4 right-4 text-xl text-brandAccent cursor-pointer hover:text-accent"
          title="Edit “Need to get in touch?”"
        />
      )}

      <div className="max-w-6xl text-center mx-auto px-8 md:px-4 space-y-4 md:space-y-8 flex flex-col justify-center items-center">
        {editingSection === "need-to-get-in-touch" ? (
          <>
            {/*** ─── EDIT MODE ───────────────────────────────────────────────── ***/}
            {/*  1) Section heading input */}
            <input
              type="text"
              className="text-primary w-full mb-2 px-3 py-2 text-2xl font-bold rounded"
              value={section.heading}
              onChange={(e) => updateSectionField("heading", e.target.value)}
            />

            {/*  2) Section copy textarea */}
            <textarea
              className="text-primary w-full mb-2 px-3 py-2 rounded"
              rows={3}
              value={section.copy}
              onChange={(e) => updateSectionField("copy", e.target.value)}
            />

            {/*  3) For each card, editable fields */}
            {section.cards.map((card, cardIdx) => (
              <div
                key={cardIdx}
                className="bg-white p-6 rounded-lg mb-8 border border-gray-200"
              >
                {/* Card icon + title input */}
                <div className="flex items-center space-x-2 mb-4">
                  {card.icon === "envelope" ? (
                    <BiEnvelope className="text-2xl text-accent" />
                  ) : (
                    <BiMessageDetail className="text-2xl text-accent" />
                  )}
                  <input
                    type="text"
                    className="text-primary flex-1 px-3 py-2 border rounded"
                    value={card.title}
                    onChange={(e) => updateCardTitle(cardIdx, e.target.value)}
                  />
                </div>

                {/** Lines list: for each line, choose type and value/text/href */}
                {card.lines.map((line, lineIdx) => {
                  // Determine current type
                  const lineType =
                    typeof line === "string"
                      ? "string"
                      : line.type &&
                        ["email", "phone", "link"].includes(line.type)
                      ? line.type
                      : "string";

                  return (
                    <div
                      key={lineIdx}
                      className="text-primary mb-2 flex items-start space-x-2"
                    >
                      {/*  a) Select box to pick line type */}
                      <select
                        value={lineType}
                        onChange={(e) =>
                          changeLineType(cardIdx, lineIdx, e.target.value)
                        }
                        className="border rounded"
                      >
                        <option value="string">Text</option>
                        <option value="email">Email</option>
                        <option value="phone">Phone</option>
                        <option value="link">Link</option>
                      </select>

                      {/*  b) Depending on type, show appropriate input(s) */}
                      {lineType === "string" && (
                        <input
                          type="text"
                          className="flex-1 px-3 py-2 border rounded"
                          value={line}
                          onChange={(e) =>
                            updateCardLine(cardIdx, lineIdx, e.target.value)
                          }
                        />
                      )}
                      {lineType === "email" && (
                        <input
                          type="email"
                          className="flex-1 px-3 py-2 border rounded"
                          placeholder="hello@zorqk.co.uk"
                          value={line.value}
                          onChange={(e) =>
                            updateCardLine(cardIdx, lineIdx, {
                              type: "email",
                              value: e.target.value,
                            })
                          }
                        />
                      )}
                      {lineType === "phone" && (
                        <input
                          type="tel"
                          className="flex-1 px-3 py-2 border rounded"
                          placeholder="+44 333 015 4101"
                          value={line.value}
                          onChange={(e) =>
                            updateCardLine(cardIdx, lineIdx, {
                              type: "phone",
                              value: e.target.value,
                            })
                          }
                        />
                      )}
                      {lineType === "link" && (
                        <div className="flex-1 grid grid-cols-2 gap-2">
                          <input
                            type="text"
                            className="px-3 py-2 border rounded"
                            placeholder="Link text"
                            value={line.text}
                            onChange={(e) =>
                              updateCardLine(cardIdx, lineIdx, {
                                type: "link",
                                text: e.target.value,
                                href: line.href || "",
                              })
                            }
                          />
                          <input
                            type="url"
                            className="px-3 py-2 border rounded"
                            placeholder="https://..."
                            value={line.href}
                            onChange={(e) =>
                              updateCardLine(cardIdx, lineIdx, {
                                type: "link",
                                text: line.text || "",
                                href: e.target.value,
                              })
                            }
                          />
                        </div>
                      )}

                      {/*  c) “Remove line” button */}
                      <button
                        onClick={() => removeCardLine(cardIdx, lineIdx)}
                        className="px-2 py-1 text-red-600 hover:text-red-800"
                        title="Remove line"
                      >
                        &times;
                      </button>
                    </div>
                  );
                })}

                {/*  d) “Add new line” button */}
                <button
                  onClick={() => addCardLine(cardIdx)}
                  className="mt-2 px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded"
                >
                  + Add line
                </button>
              </div>
            ))}

            {/* 4) Save / Cancel for entire section */}
            <div className="flex space-x-2">
              <button
                className="px-4 py-2 bg-green-600 text-white rounded"
                onClick={() => saveContent({ ...pageContent })}
              >
                Save Section
              </button>
              <button
                className="px-4 py-2 bg-gray-600 text-white rounded"
                onClick={() => setEditingSection(null)}
              >
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Floating cars */}
            <div className="hidden md:block absolute -top-52 md:-top-60 lg:-top-96 right-0 z-0">
              <img
                src={sideCars}
                alt="Cars"
                className="w-[80vw] sm:w-[70vw] md:w-[50vw] object-contain"
              />
            </div>

            {/*** ─── VIEW MODE ───────────────────────────────────────────────── ***/}
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl text-white font-bold">
              {(
                <>
                  Get in touch with our{" "}
                  <span className="underline">5* reviewed</span> team
                </>
              ) || section.heading}
            </h2>

            <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-8 md:px-20 pt-10">
              {section.cards.map((card, idx) => (
                <div
                  key={idx}
                  className="bg-white p-8 rounded-3xl shadow-md flex flex-col items-center text-center"
                >
                  <TfiEmail className="text-2xl text-primary mb-4" />
                  <h3 className="text-base md:text-lg lg:text-xl text-accent font-semibold mb-2">
                    {card.title}
                  </h3>
                  <div className="text-base md:text-lg lg:text-xl text-primary mb-1 space-y-3">
                    {card.lines.map((line, i) => renderLine(line, i))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
